import React from "react"
import PropTypes from "prop-types"
import { Dropdown } from "antd"
import { useTranslation } from "react-i18next"
import DSTab from "ui/DSTab"
import { Icon } from "ui/Icon"
import BlackoutSectionCard from "./BlackoutSectionCard"
import BlackoutRow from "./BlackoutRow"
import { BLACKOUT_SCOPES, RESOURCE_TABS } from "../constants"
import styles from "./ResourcesBlackoutCard.module.css"

/**
 * Crew and equipment blackouts share one card: sub-tabs pick which list is shown, and the
 * "Add" control is a dropdown because either kind can be created from here.
 */
const ResourcesBlackoutCard = ({ crewBlackouts, equipmentBlackouts, totalCount, activeTab, onTabChange, isLoading, isDisabled, onAdd, getMenuItems, onReviewConflicts }) => {
  const { t } = useTranslation()

  const isCrewTab = activeTab === BLACKOUT_SCOPES.CREW
  const blackouts = isCrewTab ? crewBlackouts : equipmentBlackouts

  // Each tab carries its own count, so the guide can see the other list is not empty.
  const tabCounts = { [BLACKOUT_SCOPES.CREW]: crewBlackouts.length, [BLACKOUT_SCOPES.EQUIPMENT]: equipmentBlackouts.length }
  const tabItems = RESOURCE_TABS.map((tab) => ({ key: tab.key, label: `${t(tab.labelKey, { defaultValue: tab.labelDefault })} (${tabCounts[tab.key]})` }))

  const addMenuItems = RESOURCE_TABS.map((tab) => ({
    key: tab.key,
    label: t(tab.labelKey, { defaultValue: tab.labelDefault }),
    onClick: () => onAdd(tab.key)
  }))

  const addButton = (
    <Dropdown menu={{ items: addMenuItems }} trigger={["click"]} placement="bottomRight" disabled={isDisabled}>
      <button type="button" className={styles.addButton} disabled={isDisabled}>
        <Icon name="Plus" width={16} height={16} />
        {t("settings.blackout.add", { defaultValue: "Add" })}
        <Icon name="ChevronDown" width={14} height={14} />
      </button>
    </Dropdown>
  )

  return (
    <BlackoutSectionCard
      variant="surface"
      title={`${t("settings.blackout.resources_title", { defaultValue: "Resources Blackout" })} (${totalCount})`}
      description={t("settings.blackout.resources_description", { defaultValue: "Listings are unavailable due to resource unavailability" })}
      action={addButton}
      subHeader={<DSTab items={tabItems} activeKey={activeTab} onChange={onTabChange} className={styles.tabs} />}
      isLoading={isLoading}
      isEmpty={!blackouts.length}
      emptyText={
        isCrewTab
          ? t("settings.blackout.empty_crew", { defaultValue: "No crew blackouts yet" })
          : t("settings.blackout.empty_equipment", { defaultValue: "No equipment blackouts yet" })
      }
    >
      {blackouts.map((blackout) => (
        <BlackoutRow key={blackout.id} variant="card" blackout={blackout} menuItems={getMenuItems(blackout)} onReviewConflicts={onReviewConflicts} />
      ))}
    </BlackoutSectionCard>
  )
}

ResourcesBlackoutCard.propTypes = {
  crewBlackouts: PropTypes.array.isRequired,
  equipmentBlackouts: PropTypes.array.isRequired,
  /** Crew plus equipment — the heading counts the whole section, not the visible tab. */
  totalCount: PropTypes.number.isRequired,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  getMenuItems: PropTypes.func.isRequired,
  onReviewConflicts: PropTypes.func.isRequired
}

export default ResourcesBlackoutCard
